import { reportResilienceState } from "@/components/AppResilience/resilience-store";

const FAILURE_WINDOW_MS = 10_000;
const OPEN_FOR_MS = 30_000;
const MAX_FAILURES = 5;

interface HostCircuit {
  readonly failures: number[];
  openUntil: number;
}

const circuits = new Map<string, HostCircuit>();

export class OfflineError extends Error {
  constructor() {
    super("Browser is offline");
    this.name = "OfflineError";
  }
}

export class CircuitOpenError extends Error {
  constructor(readonly host: string) {
    super(`Circuit open for ${host}`);
    this.name = "CircuitOpenError";
  }
}

export function assertCircuitClosed(input: RequestInfo | URL): void {
  const host = resolveHost(input);
  const circuit = circuits.get(host);
  if (!circuit || circuit.openUntil <= Date.now()) return;
  reportResilienceState("reconnecting", "QB-SRV-CIRCUIT");
  throw new CircuitOpenError(host);
}

export function recordServerFailure(input: RequestInfo | URL): void {
  const host = resolveHost(input);
  const now = Date.now();
  const circuit = circuits.get(host) ?? { failures: [], openUntil: 0 };
  circuit.failures.push(now);
  trimFailures(circuit.failures, now);
  if (circuit.failures.length >= MAX_FAILURES) {
    circuit.openUntil = now + OPEN_FOR_MS;
    reportResilienceState("reconnecting", "QB-SRV-CIRCUIT");
  }
  circuits.set(host, circuit);
}

export function recordFetchSuccess(input: RequestInfo | URL): void {
  circuits.delete(resolveHost(input));
  if (typeof navigator === "undefined" || navigator.onLine !== false) {
    reportResilienceState("online");
  }
}

export function __resetCircuitBreakers(): void {
  circuits.clear();
}

function trimFailures(failures: number[], now: number): void {
  const oldest = now - FAILURE_WINDOW_MS;
  while (failures[0] !== undefined && failures[0] < oldest) failures.shift();
}

function resolveHost(input: RequestInfo | URL): string {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  const origin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL(url, origin).host;
}
